import { api, ApiError } from '../lib/api.js';
import { el, render, fmtRelative, fmtDateOnly } from '../lib/dom.js';
import { pageHeader, card, kpiCard, trendMeta, button, emptyState, errorState, skeletonRows, fullRow } from '../lib/ui.js';
import { canWrite } from '../lib/session.js';

// ---------------------------------------------------------------------------
// Dashboard — the platform at a glance. Read-only; every number is a count of
// real rows from GET /api/dashboard (see dashboard.service.ts), and a trend is
// only shown when there is an earlier 30-day window to compare against.
// ---------------------------------------------------------------------------

const fmt = (n) => Number(n ?? 0).toLocaleString();

export function dashboardView({ navigate }) {
  const kpiSlot = el('div', { class: 'kpi-row' });
  const activityBody = el('tbody', {});
  const statusSlot = el('div', {});
  const trialsSlot = el('div', {});
  const usageSlot = el('div', {});

  function renderKpis(k) {
    render(
      kpiSlot,
      kpiCard({
        label: 'Total organisations',
        value: fmt(k.organisations.total),
        iconName: 'kpi-building',
        tone: 'amber',
        meta: trendMeta(k.organisations),
        title: 'Live organisations (soft-deleted ones excluded). Trend compares organisations created in the last 30 days with the 30 before.',
      }),
      kpiCard({
        label: 'Users',
        value: fmt(k.users.total),
        iconName: 'kpi-users',
        tone: 'green',
        meta: trendMeta(k.users, 'new'),
        title: 'Live accounts — a person in several organisations counts once. Trend compares new accounts in the last 30 days with the 30 before.',
      }),
      kpiCard({
        label: 'Projects',
        value: fmt(k.projects.total),
        iconName: 'kpi-projects',
        tone: 'blue',
        meta: trendMeta(k.projects),
        title: 'Every project across every organisation.',
      }),
      kpiCard({
        label: 'AI schedule runs',
        value: fmt(k.scheduleRuns.total),
        iconName: 'kpi-ai',
        tone: 'indigo',
        meta: trendMeta(k.scheduleRuns, 'runs'),
        title: 'Every AI execution against a schedule — generations, re-validations and what-ifs.',
      }),
    );
  }

  function activityRows(entries) {
    if (entries.length === 0) {
      return [fullRow(5, emptyState({ title: 'No activity yet', text: 'Activity from every organisation appears here as it happens.', compact: true }))];
    }
    return entries.map((a) =>
      el(
        'tr',
        { class: a.orgId ? 'is-clickable' : '', title: a.orgId ? `Open ${a.orgName ?? 'this organisation'}'s activity.` : null, onclick: () => a.orgId && navigate(`/orgs/${a.orgId}?tab=activity`) },
        el('td', { class: 'ellipsis', title: a.actorEmail ?? '' }, el('span', { class: 'cell-title' }, a.actorName ?? a.actorEmail ?? '—')),
        el('td', { class: 'ellipsis', title: a.orgName ?? '' }, a.orgName ?? '—'),
        el('td', {}, a.text),
        el('td', {}, el('span', { class: `module-badge module-badge--${a.module?.key ?? ''}` }, a.module?.label ?? '—')),
        el('td', { class: 'col-right nowrap muted' }, fmtRelative(a.createdAt)),
      ),
    );
  }

  function renderStatus(s) {
    const item = (label, value, tone, onClick) =>
      el(
        'div',
        { class: 'status-item' },
        el('span', { class: 'status-item__label' }, label),
        el('button', { type: 'button', class: `link-btn status-item__value status-item__value--${tone}`, onclick: onClick }, fmt(value)),
      );
    render(
      statusSlot,
      card({
        title: 'Organisation status',
        body: el(
          'div',
          { class: 'status-list' },
          item('Active (trial lifted)', s.active, 'green', () => navigate('/orgs')),
          item('On trial', s.trial, 'amber', () => navigate('/orgs')),
          item('Deactivated', s.deactivated, 'gray', () => navigate('/orgs')),
          item('Deleted', s.deleted, 'red', () => navigate('/orgs')),
        ),
      }),
    );
  }

  function renderTrials(t) {
    const urgency = (daysLeft) => {
      if (daysLeft === null) return { tag: 'Info', tone: 'blue' };
      if (daysLeft <= 0) return { tag: 'Expired', tone: 'red' };
      if (daysLeft <= 2) return { tag: 'Critical', tone: 'red' };
      if (daysLeft <= 7) return { tag: 'Soon', tone: 'amber' };
      return { tag: 'Upcoming', tone: 'blue' };
    };
    render(
      trialsSlot,
      card({
        title: 'Trials ending soon',
        subtitle: 'Next 14 days',
        actions: [button('View all', { variant: 'ghost', size: 'sm', onClick: () => navigate('/trials') })],
        body:
          t.organisations.length === 0
            ? el('p', { class: 'section-hint' }, 'No trial ends in the next 14 days.')
            : el(
                'div',
                { class: 'alert-list' },
                t.organisations.map((o) => {
                  const u = urgency(o.daysLeft);
                  return el(
                    'button',
                    { type: 'button', class: 'alert-item', title: `Open ${o.name} to extend its trial or switch it to CMAI Core.`, onclick: () => navigate(`/orgs/${o.id}?tab=settings`) },
                    el(
                      'span',
                      { class: 'alert-item__head' },
                      el('span', { class: `alert-item__tag alert-item__tag--${u.tone}` }, u.tag),
                      el('span', { class: 'alert-item__when' }, o.daysLeft === null ? '' : o.daysLeft <= 0 ? 'ended' : `${o.daysLeft} day${o.daysLeft === 1 ? '' : 's'} left`),
                    ),
                    el('span', { class: 'alert-item__text' }, `${o.name} — trial ends ${fmtDateOnly(o.trialEndsAt, o.timeZone || 'UTC')}`),
                  );
                }),
              ),
      }),
    );
  }

  function renderUsage(u, users) {
    render(
      usageSlot,
      card({
        title: 'Platform usage',
        body: el(
          'dl',
          { class: 'info-list' },
          el('div', { class: 'info-row' }, el('dt', {}, 'Users signed in, last 30 days'), el('dd', {}, fmt(users.signedInLast30Days))),
          el('div', { class: 'info-row' }, el('dt', {}, 'Coco AI prompts'), el('dd', {}, fmt(u.cocoPrompts.total))),
          el('div', { class: 'info-row' }, el('dt', {}, 'Coco AI prompts, last 30 days'), el('dd', {}, fmt(u.cocoPrompts.last30Days))),
          el(
            'div',
            { class: 'info-row' },
            el(
              'dt',
              { title: 'Root schedule runs (original generations and re-validations). Excludes XER imports and contractor-submission drafts that have never been run.' },
              'AI-validated schedule versions',
            ),
            el('dd', {}, fmt(u.scheduleVersions)),
          ),
        ),
      }),
    );
  }

  async function load() {
    render(kpiSlot, Array.from({ length: 4 }, () => kpiCard({ label: '…', value: el('span', { class: 'skeleton skeleton--mid', style: 'height:24px;width:80px' }) })));
    render(activityBody, skeletonRows(5, 5));
    try {
      const d = await api.dashboard();
      renderKpis(d.kpis);
      render(activityBody, activityRows(d.recentActivity));
      renderStatus(d.orgStatus);
      renderTrials(d.trialsEndingSoon);
      renderUsage(d.usage, d.kpis.users);
    } catch (err) {
      render(
        kpiSlot,
        el('div', { style: 'grid-column:1/-1' }, card({ body: errorState({ title: 'Failed to load the dashboard', text: err instanceof ApiError ? err.message : 'Check the API and try again.', onRetry: () => void load() }) })),
      );
      render(activityBody);
    }
  }

  void load();

  return el(
    'div',
    { class: 'stack' },
    pageHeader({
      title: 'Dashboard',
      subtitle: 'Overview of your ConstructMind platform',
      actions: [
        canWrite() ? button('Add organisation', { variant: 'primary', iconName: 'plus', onClick: () => navigate('/orgs/new'), title: 'Provision a new tenant organisation.' }) : null,
      ],
    }),
    kpiSlot,
    el(
      'div',
      { class: 'split split--wide' },
      card({
        title: 'Recent activity',
        subtitle: 'Across every organisation',
        flush: true,
        body: el(
          'div',
          { class: 'table-scroll' },
          el(
            'table',
            { class: 'table activity-table' },
            el(
              'thead',
              {},
              el(
                'tr',
                {},
                el('th', { style: 'width:20%' }, 'User'),
                el('th', { style: 'width:20%' }, 'Organisation'),
                el('th', {}, 'Action'),
                el('th', { style: 'width:120px' }, 'Module'),
                el('th', { class: 'col-right', style: 'width:110px' }, 'Time'),
              ),
            ),
            activityBody,
          ),
        ),
      }),
      el('div', { class: 'stack' }, statusSlot, trialsSlot, usageSlot),
    ),
  );
}
